import { useCallback, useEffect, useRef } from "react";

import {
  type GlobalFilters,
  useGlobalFilters,
  useSetGlobalFilters,
  validateGlobalFilters,
} from "./globalFilters";

// Per-page filters (ADR 0031 follow-up). The toolbar used to host a single
// filter set retained across the whole app, which read as one site-wide filter
// and made clearing impossible (the retainSearchParams middleware re-injected
// any key the user tried to drop). Filters now live on each page: every page
// owns its own URL search params and remembers its last choice independently in
// sessionStorage, so leaving and returning restores that page's filters without
// bleeding into its neighbours.
export type PageFilterScope = "dashboard" | "trades" | "analytics";

type RememberedKey = keyof GlobalFilters;

// Which filter keys each page persists. The dashboard's date range is owned by
// the preset bar (remembered as a relative preset, not absolute dates), so the
// dashboard only remembers account/tag here.
const REMEMBERED_KEYS: Record<PageFilterScope, readonly RememberedKey[]> = {
  dashboard: ["account_id", "tag_id"],
  trades: ["since", "until"],
  analytics: ["since", "until", "account_id", "tag_id"],
};

const STORAGE_PREFIX = "pageFilters:";

function storageKey(scope: PageFilterScope): string {
  return `${STORAGE_PREFIX}${scope}`;
}

function pick(
  filters: GlobalFilters,
  keys: readonly RememberedKey[],
): GlobalFilters {
  const out: GlobalFilters = {};
  for (const key of keys) {
    const value = filters[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out;
}

export function readRememberedFilters(scope: PageFilterScope): GlobalFilters {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey(scope));
    if (raw === null) {
      return {};
    }
    const parsed = validateGlobalFilters(
      JSON.parse(raw) as Record<string, unknown>,
    );
    return pick(parsed, REMEMBERED_KEYS[scope]);
  } catch {
    return {};
  }
}

function writeRememberedFilters(
  scope: PageFilterScope,
  filters: GlobalFilters,
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey(scope), JSON.stringify(filters));
  } catch {
    // Best-effort: a private-mode browser simply re-derives filters next visit.
  }
}

export interface PageFilters {
  filters: GlobalFilters;
  setFilters: (patch: Partial<GlobalFilters>) => void;
}

export function usePageFilters(scope: PageFilterScope): PageFilters {
  const filters = useGlobalFilters();
  const rawSet = useSetGlobalFilters();
  const keys = REMEMBERED_KEYS[scope];
  const restoredRef = useRef(false);

  // One-shot restore: on first mount, if the URL carries none of this page's
  // remembered keys, re-apply what the user last chose here. A deep link or
  // in-app drill-through (which carries its own search) wins over the memory.
  useEffect(() => {
    if (restoredRef.current) {
      return;
    }
    restoredRef.current = true;
    const hasUrlValue = keys.some((key) => filters[key] !== undefined);
    if (hasUrlValue) {
      return;
    }
    const remembered = readRememberedFilters(scope);
    if (Object.keys(remembered).length > 0) {
      rawSet(remembered);
    }
    // Mount-only: restore reads the first-render filters intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilters = useCallback(
    (patch: Partial<GlobalFilters>) => {
      rawSet(patch);
      // Persist the post-patch value of the remembered keys (including clears),
      // so returning to this page restores exactly what the user last chose.
      const next = pick(filters, keys);
      for (const key of keys) {
        if (key in patch) {
          const value = patch[key];
          if (value === undefined || value === "") {
            delete next[key];
          } else {
            next[key] = value;
          }
        }
      }
      writeRememberedFilters(scope, next);
    },
    [rawSet, filters, scope, keys],
  );

  return { filters, setFilters };
}
